import { saveFile } from '@modules/file-menagement/fileUtils';
import { Tooltip, Typography, styled } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { AiChatMessage } from 'src/@types/ai';
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import { AIInputStateControl } from 'src/features/AIInput';
import useCopyToClipboard from 'src/hooks/useCopyToClipboard';
import { useLocales } from 'src/locales';
import { useAIAudioPlayer } from './useAIAudioPlayer';
import { useAIAudioPlayerState } from './useAIAudioPlayerState';

type Props = {
  message: AiChatMessage;
  store: AIInputStateControl;
};

export const AIMessageAudioPlayer = ({ message, store }: Props) => {
  const [audioFile, setAudioFile] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const { getAudio, isPlaying, isLoading } = useAIAudioPlayerState();
  const { play, stop, getAudioFromText } = useAIAudioPlayer(store);
  const { translate } = useLocales();

  const { copy } = useCopyToClipboard();
  const { enqueueSnackbar } = useSnackbar();

  const isLoadingTextToSpeech = useMemo(
    () => isLoading?.[message._id],
    [isLoading, message._id],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const audio = getAudio(message._id);
    if (!audio) return;
    setAudioFile(audio);
  }, [message._id, isLoadingTextToSpeech]);

  const handlePlay = async () => {
    let audio = audioFile;
    if (!audio) {
      audio = await getAudioFromText(message._id, message.content);
      setAudioFile(audio);
    }
    play(audio, message._id);
  };

  const handleStop = () => {
    stop();
  };

  const handleDownload = async () => {
    if (!audioFile) return;
    setIsDownloading(true);
    await saveFile('ai_response.aac', audioFile);
    setIsDownloading(false);
  };

  const handleCopy = async () => {
    await copy(message.content);
    enqueueSnackbar(translate('shared.buttons.message_copied_to_clipboard'));
  };

  if (!message.content) return null;

  return (
    <StyledPlayerContainer className="player-container">
      <Tooltip title={translate('shared.buttons.copy_message')} placement="top">
        <PlayerIcon icon="tabler:copy" onClick={handleCopy} />
      </Tooltip>

      {isLoadingTextToSpeech ? (
        <>
          <PlayerIcon icon="eos-icons:loading" />
          <Typography variant="caption" color="text.secondary">
            {translate('share.loading.loading_audio_response')}
          </Typography>
        </>
      ) : isPlaying === message._id ? (
        <Tooltip
          title={translate('copera_ai.input.stop_audio_response')}
          placement="top"
        >
          <PlayerIcon icon="tabler:player-stop" onClick={handleStop} />
        </Tooltip>
      ) : (
        <Tooltip
          title={translate('copera_ai.input.listen_audio_response')}
          placement="top"
        >
          <PlayerIcon icon="tabler:play" onClick={handlePlay} />
        </Tooltip>
      )}
      {audioFile && (
        <>
          {isDownloading ? (
            <PlayerIcon icon="eos-icons:loading" />
          ) : (
            <Tooltip
              title={translate('shared.download.download_audio')}
              placement="top"
            >
              <PlayerIcon icon="tabler:download" onClick={handleDownload} />
            </Tooltip>
          )}
        </>
      )}
    </StyledPlayerContainer>
  );
};

const StyledPlayerContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  marginTop: theme.spacing(0.5),
  gap: theme.spacing(1),
  opacity: 0,
  '& .iconify': {
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.text.primary,
    },
  },
}));

const PlayerIcon = styled(Iconify)(({ theme }) => ({
  width: theme.spacing(2),
  height: theme.spacing(2),
}));
